"use client"
import Image from 'next/image';

import {
  Upload,
  X
} from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActionButtons from './ActionButtons';
import BlogPreview from './BlogPreview';
import ContentEditor from './ContentEditor';
import FloatingChecklist, { ChecklistItem } from './FloatingChecklist';
import InstagramManagementCard, { type InstagramManagementValue } from './instagram/InstagramManagementCard';
import LinkModal from './LinkModal';
import SEOSettings from './SEOSettings';
import BlogSocialMediaPostingSection from './BlogSocialMediaPostingSection';
import {
  extraImagesForCompose,
  socialPostingCaptionForCompose,
  socialPostingEnabledForCompose,
  socialPostingTagsForCompose,
} from '@/lib/social-media/image-fetch-urls';
import {
  datetimeLocalToIsoOrNull,
  toDatetimeLocalValue,
} from '@/lib/social-media/facebook-schedule-fields';
import { ToastContainer, useToast } from '@/components/ui/ToastContainer';
import { compressImageIfNeeded, compressImage } from '@/lib/utils/imageCompression';

// TipTap imports
import { Mark, mergeAttributes } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorView } from '@tiptap/pm/view';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

// removed unused User interface

interface BlogEditorProps {
  blogId?: string;
  blogType?: 'Post' | 'News' | 'Thought & Leadership';
}

// Custom extension for keyword highlighting
const KeywordHighlight = Mark.create({
  name: 'keywordHighlight',

  addOptions() {
    return {
      keywords: [],
    };
  },

  addAttributes() {
    return {
      keyword: {
        default: null,
        parseHTML: element => element.getAttribute('data-keyword'),
        renderHTML: attributes => {
          if (!attributes.keyword) {
            return {};
          }

          return {
            'data-keyword': attributes.keyword,
            'class': 'keyword-highlight',
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-keyword]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
});

// Categories are now loaded dynamically from admin Categories module

export default function BlogEditor({ blogId, blogType: initialBlogType }: BlogEditorProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const { showToast, removeToast, toasts } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    featuredImageAlt: '',
    author: '',
    editor: '',
    tags: [] as string[],
    status: 'Draft',
    publishDate: '',
    publishTime: '',
    metaTitle: '',
    metaDescription: '',
    focusKeywords: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    schemaMarkup: '',
    enableSchema: true,
    blogType: (initialBlogType || 'Post') as 'Post' | 'News' | 'Thought & Leadership',
    socialMediaPostingEnabled: false,
    socialMediaPostingScheduledAt: '',
    socialMediaPostingPublishedAt: '',
    socialMediaPostingCaption: '',
    socialMediaPostingTagsStr: '',
    socialMediaPostingUseFeaturedImage: true,
    socialMediaPostingExtraImageUrls: [] as string[],
    facebookBlogAutoPostId: '',
    facebookBlogAutoPostError: '',
    instagramPostingEnabled: false,
    instagramPublishType: 'scheduled' as 'now' | 'scheduled',
    instagramPostingCaption: '',
    instagramPostingTagsStr: '',
    instagramPostingUseFeaturedImage: true,
    instagramPostingImageUrl: '',
    instagramPostingScheduledAt: '',
    instagramPostingStatus: 'draft',
    instagramPostingPublishedAt: '',
    instagramMediaId: '',
    instagramPermalink: '',
    instagramUsername: '',
    instagramLastSyncAt: '',
  });

  const [showPreview, setShowPreview] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [availableAuthors, setAvailableAuthors] = useState<Array<{ id: string; name: string; fullName: string }>>([]);
  const [lockAuthorForEditor, setLockAuthorForEditor] = useState(false);
  const [allCategories, setAllCategories] = useState<Array<{ name: string; type?: string }>>([]);
  const sessionEditorName = useMemo(() => {
    const name = (session as unknown as { user?: { name?: string; email?: string } })?.user?.name;
    const email = (session as unknown as { user?: { name?: string; email?: string } })?.user?.email;
    return name || email || '';
  }, [session]);
  const sessionRole = useMemo(() => {
    return (session as unknown as { user?: { role?: 'Admin' | 'Editor' | 'Author' } })?.user?.role || 'Author';
  }, [session]);
  const effectiveAuthorName = formData.author || (!blogId && sessionRole === 'Author' ? sessionEditorName : '');
  const effectiveEditorName = formData.editor || (!blogId && sessionRole !== 'Author' ? sessionEditorName : '');

  // SEO completion status
  const [seoComplete, setSeoComplete] = useState(false);

  // Keyword density analysis
  const [keywordDensity, setKeywordDensity] = useState<{
    keyword: string;
    count: number;
    density: number;
    status: 'optimal' | 'too-low' | 'too-high';
  }[]>([]);

  // TipTap Editor setup
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Underline,
      Subscript,
      Superscript,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      KeywordHighlight,
      ImageExtension.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-800 underline',
          // Allow rel to be set per-link for SEO control
          rel: null,
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({
        placeholder: 'Write your blog content here...',
      }),
    ],
    content: formData.content || '<p></p>',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setFormData(prev => ({ ...prev, content: html }));

      // Real-time validation
      validateField('content', html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose-base lg:prose-lg max-w-none focus:outline-none min-h-[300px] p-4 keyword-editor',
      },
      handleKeyDown: (_view: EditorView, event: KeyboardEvent) => {
        if (!editor) return false;

        // Bold: Ctrl+B or Cmd+B
        if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
          event.preventDefault();
          editor.chain().focus().toggleBold().run();
          return true;
        }

        // Italic: Ctrl+I or Cmd+I
        if ((event.ctrlKey || event.metaKey) && event.key === 'i') {
          event.preventDefault();
          editor.chain().focus().toggleItalic().run();
          return true;
        }

        // Underline: Ctrl+U or Cmd+U
        if ((event.ctrlKey || event.metaKey) && event.key === 'u') {
          event.preventDefault();
          editor.chain().focus().toggleUnderline().run();
          return true;
        }

        // Code: Ctrl+` or Cmd+`
        if ((event.ctrlKey || event.metaKey) && event.key === '`') {
          event.preventDefault();
          editor.chain().focus().toggleCode().run();
          return true;
        }

        // Code Block: Ctrl+Shift+`
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === '~') {
          event.preventDefault();
          editor.chain().focus().toggleCodeBlock().run();
          return true;
        }

        // Subscript: Ctrl+,
        if ((event.ctrlKey || event.metaKey) && event.key === ',') {
          event.preventDefault();
          editor.chain().focus().toggleSubscript().run();
          return true;
        }

        // Superscript: Ctrl+.
        if ((event.ctrlKey || event.metaKey) && event.key === '.') {
          event.preventDefault();
          editor.chain().focus().toggleSuperscript().run();
          return true;
        }

        // Clear formatting: Ctrl+\
        if ((event.ctrlKey || event.metaKey) && event.key === '\\') {
          event.preventDefault();
          editor.chain().focus().clearNodes().unsetAllMarks().run();
          return true;
        }

        // Undo: Ctrl+Z or Cmd+Z
        if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
          event.preventDefault();
          editor.chain().focus().undo().run();
          return true;
        }

        // Redo: Ctrl+Y, Ctrl+Shift+Z, or Cmd+Y, Cmd+Shift+Z
        if (
          ((event.ctrlKey || event.metaKey) && event.key === 'y') ||
          ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Z')
        ) {
          event.preventDefault();
          editor.chain().focus().redo().run();
          return true;
        }

        // Link: Ctrl+K or Cmd+K
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
          event.preventDefault();
          // Open custom link modal instead of browser prompt
          const attrs = editor.getAttributes('link');
          setInitialLinkUrl((attrs?.href as string) || '');
          setInitialLinkRel((attrs?.rel as string) ?? null);
          setInitialOpenInNewTab(((attrs?.target as string) || '') === '_blank');
          setIsLinkModalOpen(true);
          return true;
        }

        return false;
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile();
            if (file) {
              // Validate file type
              const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
              if (!allowedTypes.includes(file.type)) {
                event.preventDefault();
                showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
                return true;
              }

              // Prevent default and handle compression/upload manually
              event.preventDefault();
              
              // Handle upload asynchronously with compression
              (async () => {
                try {
                  // Compress image if needed (will compress to below 1MB)
                  let compressedFile = await compressImageIfNeeded(file, showToast);
                  
                  // Verify compression worked
                  const maxSizeBytes = 1 * 1024 * 1024; // 1MB
                  if (compressedFile.size > maxSizeBytes) {
                    console.warn(`Compressed file is still ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB, attempting more aggressive compression...`);
                    compressedFile = await compressImage(compressedFile, 0.95, 0.5);
                    if (compressedFile.size > maxSizeBytes) {
                      throw new Error(`Unable to compress image below 1MB. Final size: ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
                    }
                  }

                  const formDataUpload = new FormData();
                  formDataUpload.append('file', compressedFile);

                  const response = await fetch('/api/upload/blogs', {
                    method: 'POST',
                    body: formDataUpload,
                  });

                  if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to upload image');
                  }

                  const result = await response.json();

                  // Insert image into editor
                  if (editor && result.fileUrl) {
                    editor.chain().focus().setImage({ src: result.fileUrl }).run();
                    showToast('Image uploaded successfully!', 'success');
                  }
                } catch (err) {
                  console.error('Image upload error:', err);
                  showToast(err instanceof Error ? err.message : 'Failed to upload image', 'error');
                }
              })();
              
              return true;
            }
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;

        event.preventDefault();

        // Handle each image file
        for (const file of imageFiles) {
          // Validate file type
          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
          if (!allowedTypes.includes(file.type)) {
            showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
            continue;
          }

          // Handle upload asynchronously with compression
          (async () => {
            try {
              // Compress image if needed (will compress to below 1MB)
              let compressedFile = await compressImageIfNeeded(file, showToast);
              
              // Verify compression worked
              const maxSizeBytes = 1 * 1024 * 1024; // 1MB
              if (compressedFile.size > maxSizeBytes) {
                console.warn(`Compressed file is still ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB, attempting more aggressive compression...`);
                compressedFile = await compressImage(compressedFile, 0.95, 0.5);
                if (compressedFile.size > maxSizeBytes) {
                  throw new Error(`Unable to compress image below 1MB. Final size: ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
                }
              }

              const formDataUpload = new FormData();
              formDataUpload.append('file', compressedFile);

              const response = await fetch('/api/upload/blogs', {
                method: 'POST',
                body: formDataUpload,
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to upload image');
              }

              const result = await response.json();

              // Insert image into editor
              if (editor && result.fileUrl) {
                editor.chain().focus().setImage({ src: result.fileUrl }).run();
                showToast('Image uploaded successfully!', 'success');
              }
            } catch (err) {
              console.error('Image upload error:', err);
              showToast(err instanceof Error ? err.message : 'Failed to upload image', 'error');
            }
          })();
        }

        return true;
      },
      handleDOMEvents: {
        click: (_view, event) => {
          const target = event.target as HTMLElement | null;
          if (target && target.closest('a')) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }
          return false;
        },
      },
    },
    immediatelyRender: false, // Prevent SSR hydration mismatches
  });
  const editorRef = useRef(editor);

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Update editor content when formData.content changes (for loading existing blogs)
  useEffect(() => {
    if (editor && formData.content !== editor.getHTML()) {
      editor.commands.setContent(formData.content || '<p></p>');
    }
  }, [editor, formData.content]);

  // When an Author-created post has been assigned to this Editor via workflow,
  // lock the Author field so the editor cannot change it.
  useEffect(() => {
    const currentRole = sessionRole;
    const editorId = (session as unknown as { user?: { id?: string } })?.user?.id;

    if (!blogId || currentRole !== 'Editor' || !editorId) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const params = new URLSearchParams({
          postId: blogId,
          postType: 'blog',
          status: 'editor_assigned_pending_review',
          limit: '1'
        });

        const res = await fetch(`/api/admin/posts/workflow?${params.toString()}`, {
          credentials: 'include'
        });
        if (!res.ok) return;

        const data = await res.json();
        if (!data?.success || !Array.isArray(data.requests) || data.requests.length === 0) return;

        const req = data.requests[0] as {
          requesterRole?: 'Editor' | 'Author';
          assignedEditorId?: string | null;
          authorName?: string;
        };

        if (!cancelled && req.requesterRole === 'Author' && req.assignedEditorId === editorId) {
          setLockAuthorForEditor(true);
          if (req.authorName) {
            setFormData(prev => (prev.author ? prev : { ...prev, author: req.authorName! }));
          }
        }
      } catch (err) {
        console.warn('Failed to check workflow for author lock:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blogId, session, sessionRole]);

  // Ensure editor is ready before allowing uploads
  const editorReady = Boolean(editor);

  // Link modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [initialLinkUrl, setInitialLinkUrl] = useState<string>('');
  const [initialLinkRel, setInitialLinkRel] = useState<string | null>(null);
  const [initialOpenInNewTab, setInitialOpenInNewTab] = useState<boolean>(true);

  const onConfirmLink = ({ url, rel, openInNewTab }: { url: string; rel: string | null; openInNewTab: boolean }) => {
    try {
      const attrs: { href: string; target?: string | null; rel?: string | null } = { href: url };
      if (rel !== null) attrs.rel = rel;
      if (openInNewTab) attrs.target = '_blank';
      editor?.chain().focus().extendMarkRange('link').setLink(attrs).run();
    } catch (e) {
      console.error('Error inserting link:', e);
      alert('Failed to insert link');
    } finally {
      setIsLinkModalOpen(false);
    }
  };

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Function declarations
  const loadBlogData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Determine API endpoint based on initialBlogType
      let apiUrl = `/api/admin/blogs/${blogId}`;
      let contentType = 'blog';

      if (initialBlogType === 'Thought & Leadership') {
        apiUrl = `/api/admin/thought-leadership/${blogId}`;
        contentType = 'thought leadership';
      } else if (initialBlogType === 'News') {
        apiUrl = `/api/admin/news-pr/${blogId}`;
        contentType = 'news';
      }

      const response = await fetch(apiUrl);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || `Failed to fetch ${contentType} data: ${response.status} ${response.statusText}`;
        console.error('Failed to load data:', errorMsg);
        throw new Error(errorMsg);
      }

      const response_data = await response.json();
      const blog = response_data.thoughtLeadership || response_data.newsPr || response_data.blog; // Extract blog data from nested property

      if (!blog) {
        throw new Error(`${contentType} not found`);
      }


      // Update form data with blog data
      setFormData({
        title: blog.title || '',
        slug: blog.slug || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        featuredImage: blog.featuredImage || '',
        featuredImageAlt: blog.featuredImageAlt || '',
        author: blog.author || '',
        editor: blog.editor || '',
        tags: blog.tags || [],
        status: blog.status || 'Draft',
        publishDate: blog.publishDate ? new Date(blog.publishDate).toISOString().split('T')[0] : '',
        publishTime: blog.publishDate ? new Date(blog.publishDate).toTimeString().slice(0, 5) : '',
        metaTitle: blog.metaTitle || '',
        metaDescription: blog.metaDescription || '',
        focusKeywords: blog.focusKeywords || '',
        canonicalUrl: blog.canonicalUrl || '',
        ogTitle: blog.ogTitle || '',
        ogDescription: blog.ogDescription || '',
        ogImage: blog.ogImage || '',
        twitterTitle: blog.twitterTitle || '',
        twitterDescription: blog.twitterDescription || '',
        twitterImage: blog.twitterImage || '',
        schemaMarkup: blog.schemaMarkup || '',
        enableSchema: blog.enableSchema !== false,
        blogType: (blog.blogType || initialBlogType || 'Post') as 'Post' | 'News' | 'Thought & Leadership',
        socialMediaPostingEnabled: socialPostingEnabledForCompose(blog),
        socialMediaPostingScheduledAt: toDatetimeLocalValue(blog.socialMediaPostingScheduledAt),
        socialMediaPostingPublishedAt: blog.socialMediaPostingPublishedAt || '',
        socialMediaPostingCaption: socialPostingCaptionForCompose(blog),
        socialMediaPostingTagsStr: socialPostingTagsForCompose(blog).join(', '),
        socialMediaPostingUseFeaturedImage: blog.socialMediaPostingUseFeaturedImage !== false,
        socialMediaPostingExtraImageUrls: extraImagesForCompose(blog),
        facebookBlogAutoPostId: blog.facebookBlogAutoPostId || '',
        facebookBlogAutoPostError: (blog.facebookBlogAutoPostError as string) || '',
        instagramPostingEnabled: blog.instagramPostingEnabled === true,
        instagramPublishType: 'scheduled',
        instagramPostingCaption: blog.instagramPostingCaption || '',
        instagramPostingTagsStr: Array.isArray(blog.instagramPostingTags)
          ? blog.instagramPostingTags.join(', ')
          : '',
        instagramPostingUseFeaturedImage: blog.instagramPostingUseFeaturedImage !== false,
        instagramPostingImageUrl: blog.instagramPostingImageUrl || '',
        instagramPostingScheduledAt: toDatetimeLocalValue(blog.instagramPostingScheduledAt),
        instagramPostingStatus: blog.instagramPostingStatus || 'draft',
        instagramPostingPublishedAt: blog.instagramPostingPublishedAt || '',
        instagramMediaId: blog.instagramMediaId || '',
        instagramPermalink: blog.instagramPermalink || '',
        instagramUsername: blog.instagramUsername || '',
        instagramLastSyncAt: blog.instagramLastSyncAt || '',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load content data';
      console.error('Error loading blog data:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [blogId, initialBlogType]);

  const loadAuthors = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/authors/personal-data');
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.authors)) {
          const authorsList = data.authors
            .filter((author: { personalData?: { firstName?: string; lastName?: string } | null }) => {
              // Include authors that have personalData
              return author.personalData !== null && author.personalData !== undefined;
            })
            .map((author: { id: string; name: string; personalData?: { firstName?: string; lastName?: string } | null }) => {
              const firstName = author.personalData?.firstName?.trim() || '';
              const lastName = author.personalData?.lastName?.trim() || '';
              const fullName = `${firstName} ${lastName}`.trim();
              // Use fullName if available, otherwise fall back to author.name
              const displayName = fullName || author.name || '';
              return {
                id: author.id,
                name: author.name,
                fullName: displayName
              };
            })
            .filter((author: { fullName: string; name: string }) => {
              // Only filter out if both fullName and name are empty
              return author.fullName.trim().length > 0 || author.name.trim().length > 0;
            });
          setAvailableAuthors(authorsList);
        } else {
          console.warn('Invalid authors data structure:', data);
        }
      } else {
        const errorText = await response.text();
        console.warn('Failed to fetch authors:', response.status, errorText);
      }
    } catch (err) {
      console.error('Failed to load authors from API:', err);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '1000', status: 'active' });
      // Add cache-busting timestamp to ensure fresh data
      params.append('_t', Date.now().toString());
      const response = await fetch(`/api/admin/categories?${params.toString()}`, {
        cache: 'no-store', // Prevent Next.js/browser caching
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.categories && Array.isArray(data.categories)) {
          // Store full category objects with type information
          const categoriesWithType = data.categories.map((c: { name: string; type?: string }) => ({
            name: c.name,
            type: c.type
          })).filter((c: { name: string }) => c.name);
          setAllCategories(categoriesWithType);
        }
      } else {
        console.warn('Failed to fetch categories');
      }
    } catch (err) {
      console.warn('Failed to load categories from API:', err);
    }
  }, []);

  // Filter categories based on selected blogType
  const filteredCategories = useMemo(() => {
    if (formData.blogType === 'News') {
      // Show only categories with type "news"
      return allCategories.filter((cat: { name: string; type?: string }) => cat.type === 'news');
    } else if (formData.blogType === 'Thought & Leadership') {
      // Show only categories with type "thought & leadership"
      return allCategories.filter((cat: { name: string; type?: string }) => cat.type === 'thought & leadership');
    } else if (formData.blogType === 'Post') {
      // Show categories with type "blogs" or no type (for backward compatibility)
      return allCategories.filter((cat: { name: string; type?: string }) =>
        !cat.type || cat.type === 'blogs'
      );
    }
    // Fallback: show all categories
    return allCategories;
  }, [allCategories, formData.blogType]);

  // Clear category selection if it doesn't match the new blogType
  useEffect(() => {
    if (formData.tags.length > 0) {
      const selectedCategory = formData.tags[0];
      const categoryExists = filteredCategories.some((cat: { name: string }) => cat.name === selectedCategory);
      if (!categoryExists) {
        // Clear the selection if it's no longer valid
         
        setFormData(prev => ({ ...prev, tags: [] }));
      }
    }
  }, [formData.blogType, filteredCategories, formData.tags]);

  const highlightKeywordsInEditor = useCallback(() => {
    const currentEditor = editorRef.current;
    if (!currentEditor || !formData.focusKeywords.trim()) return;

    const keywords = [...new Set(
      formData.focusKeywords
        .split(',')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 1) // Same filter as density calculation
    )];

    if (keywords.length === 0) return;

    try {
      // Clear existing highlights first
      currentEditor.commands.unsetMark('keywordHighlight');

      // Get plain text from editor for consistent processing
      const plainText = currentEditor.getText();

      if (!plainText.trim()) return;

      // Find all keyword positions
      const allPositions: { keyword: string; from: number; to: number }[] = [];

      keywords.forEach(keyword => {
        try {
          const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
          let match;
          while ((match = regex.exec(plainText)) !== null) {
            allPositions.push({
              keyword,
              from: match.index,
              to: match.index + match[0].length
            });
          }
        } catch (error) {
          console.warn(`Error creating regex for keyword "${keyword}":`, error);
        }
      });

      // Sort positions by index (descending) to avoid offset issues
      allPositions.sort((a, b) => b.from - a.from);

      // Apply highlights
      allPositions.forEach(({ keyword, from, to }) => {
        try {
          currentEditor.commands.setTextSelection({ from, to });
          currentEditor.commands.setMark('keywordHighlight', { keyword });
        } catch (error) {
          console.warn(`Error highlighting "${keyword}" at position ${from}-${to}:`, error);
        }
      });

      // Reset selection to end
      currentEditor.commands.setTextSelection(currentEditor.state.doc.content.size);
    } catch (error) {
      console.warn('Error highlighting keywords:', error);
    }
  }, [formData.focusKeywords]);

  const calculateKeywordDensity = useCallback(() => {
    const currentEditor = editorRef.current;
    if (!formData.focusKeywords.trim() || !formData.content) {
      setKeywordDensity([]);
      return;
    }

    // Parse keywords (comma-separated, trim whitespace, remove duplicates)
    const keywords = [...new Set(
      formData.focusKeywords
        .split(',')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 1)
    )];

    if (keywords.length === 0) {
      setKeywordDensity([]);
      return;
    }

    // Use editor plain text when available to match UI stats; fallback to HTML-stripped content
    const sourceText = currentEditor ? currentEditor.getText() : formData.content.replace(/<[^>]*>/g, ' ');

    // Normalize: lowercase, remove punctuation, collapse spaces
    const normalizedText = sourceText
      .toLowerCase()
      .replace(/[^\w\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const words = normalizedText.length ? normalizedText.split(' ') : [];
    const totalWords = words.length;

    if (totalWords === 0) {
      setKeywordDensity([]);
      return;
    }

    // Calculate density for each keyword (supports single words and phrases)
    const densityData = keywords.map(rawKeyword => {
      const normalizedKeyword = rawKeyword
        .toLowerCase()
        .replace(/[^\w\s]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      let count = 0;
      if (!normalizedKeyword) {
        return { keyword: rawKeyword, count: 0, density: 0, status: 'too-low' as const };
      }

      if (normalizedKeyword.includes(' ')) {
        const phrase = normalizedKeyword.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        const regex = new RegExp(`\\b${phrase}\\b`, 'g');
        const matches = normalizedText.match(regex);
        count = matches ? matches.length : 0;
      } else {
        const target = normalizedKeyword;
        for (let i = 0; i < words.length; i++) {
          if (words[i] === target) count++;
        }
      }

      const density = (count / totalWords) * 100;

      let status: 'optimal' | 'too-low' | 'too-high';
      if (density < 1) status = 'too-low';
      else if (density > 3) status = 'too-high';
      else status = 'optimal';

      return {
        keyword: rawKeyword,
        count,
        density: Math.round(density * 100) / 100,
        status,
      };
    });

    setKeywordDensity(densityData);
  }, [formData.focusKeywords, formData.content]);

  // Load blog data if editing
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (blogId) {
        loadBlogData();
      }
      loadAuthors();
      loadCategories();
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [blogId, loadBlogData, loadCategories, loadAuthors]);

  // Refresh categories when window regains focus (e.g., after updating categories in another tab)
  useEffect(() => {
    const handleFocus = () => {
      loadCategories();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadCategories]);

  // Load latest workflow state (rework/reject) for this post for current user
  useEffect(() => {
    const loadWorkflowNotice = async () => {
      if (!blogId || !session) return;
      try {
        const params = new URLSearchParams({ postId: blogId, limit: '1' });
        const res = await fetch(`/api/admin/posts/workflow?${params.toString()}`, {
          cache: 'no-store'
        });
        if (!res.ok) return;
        const data = await res.json();
        const req = Array.isArray(data.requests) && data.requests.length > 0
          ? data.requests[0]
          : null;
        if (!req) return;

        // Find most recent history entry with reason if any
        const history = Array.isArray(req.history) ? req.history : [];
        const lastWithReason = [...history].reverse().find(
          (h: { type?: string; reason?: string }) => (h.type === 'rework_requested' || h.type === 'rejected') && h.reason
        );

        if (req.status === 'rework_requested' && lastWithReason?.reason) {
          setInfoMessage(`Rework requested by Admin: ${lastWithReason.reason}`);
        } else if (req.status === 'rejected' && lastWithReason?.reason) {
          setInfoMessage(`This post was rejected by Admin: ${lastWithReason.reason}`);
        } else {
          setInfoMessage(null);
        }
      } catch {
        // Non-blocking: ignore errors
      }
    };

    loadWorkflowNotice();
  }, [blogId, session]);


  // Calculate keyword density when keywords or content change
  useEffect(() => {
    // Small delay to ensure editor content is updated
    const timeoutId = setTimeout(() => {
      calculateKeywordDensity();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [formData.focusKeywords, formData.content, editor, calculateKeywordDensity]);

  // Highlight keywords in editor when they change
  useEffect(() => {
    if (editor && formData.focusKeywords.trim()) {
      highlightKeywordsInEditor();
    }
  }, [formData.focusKeywords, editor, highlightKeywordsInEditor]);

  const validateSEOComplete = useCallback(() => {
    const missingFields: string[] = [];

    // For Thought Leadership, only require basic SEO fields
    if (formData.blogType === 'Thought & Leadership') {
      if (!formData.metaTitle.trim()) {
        missingFields.push('Meta Title');
      }
      if (!formData.metaDescription.trim()) {
        missingFields.push('Meta Description');
      }
      // Focus keywords are optional for thought leadership
      // if (!formData.focusKeywords.trim()) {
      //   missingFields.push('Focus Keywords');
      // }
    } else {
      // For blogs and news, require all SEO fields
      if (!formData.metaTitle.trim()) {
        missingFields.push('Meta Title');
      }
      if (!formData.metaDescription.trim()) {
        missingFields.push('Meta Description');
      }
      if (!formData.focusKeywords.trim()) {
        missingFields.push('Focus Keywords');
      }
      if (!formData.ogTitle.trim()) {
        missingFields.push('Open Graph Title');
      }
      if (!formData.ogDescription.trim()) {
        missingFields.push('Open Graph Description');
      }
      if (!formData.ogImage.trim()) {
        missingFields.push('Open Graph Image');
      }
      if (!formData.twitterTitle.trim()) {
        missingFields.push('Twitter Title');
      }
      if (!formData.twitterDescription.trim()) {
        missingFields.push('Twitter Description');
      }
      if (!formData.twitterImage.trim()) {
        missingFields.push('Twitter Image');
      }
      if (!formData.schemaMarkup.trim()) {
        missingFields.push('Schema Markup');
      }
    }

    return {
      isComplete: missingFields.length === 0,
      missingFields
    };
  }, [formData.metaTitle, formData.metaDescription, formData.focusKeywords, formData.ogTitle, formData.ogDescription, formData.ogImage, formData.twitterTitle, formData.twitterDescription, formData.twitterImage, formData.schemaMarkup, formData.blogType]);

  // Update SEO completion status when SEO fields change
  useEffect(() => {
    const seoValidation = validateSEOComplete();
     
    setSeoComplete(seoValidation.isComplete);
  }, [formData.metaTitle, formData.metaDescription, formData.focusKeywords, formData.ogTitle, formData.ogDescription, formData.ogImage, formData.twitterTitle, formData.twitterDescription, formData.twitterImage, formData.enableSchema, formData.schemaMarkup, validateSEOComplete]);

  // Auto-fill SEO fields when title changes
  useEffect(() => {
    if (formData.title.trim() && formData.blogType === 'Thought & Leadership') {
      const title = formData.title.trim();

      // Auto-fill meta title if empty
      if (!formData.metaTitle.trim()) {
         
        setFormData(prev => ({
          ...prev,
          metaTitle: title.length > 60 ? title.substring(0, 57) + '...' : title
        }));
      }

      // Auto-fill OG title if empty
      if (!formData.ogTitle.trim()) {
        setFormData(prev => ({
          ...prev,
          ogTitle: title.length > 60 ? title.substring(0, 57) + '...' : title
        }));
      }

      // Auto-fill Twitter title if empty
      if (!formData.twitterTitle.trim()) {
        setFormData(prev => ({
          ...prev,
          twitterTitle: title.length > 60 ? title.substring(0, 57) + '...' : title
        }));
      }
    }
  }, [formData.title, formData.blogType, formData.metaTitle, formData.ogTitle, formData.twitterTitle]);

  const getPlainTextContent = useCallback(() => {
    if (!formData.content) return '';
    const temp = document.createElement('div');
    temp.innerHTML = formData.content;
    return (temp.textContent || temp.innerText || '').trim();
  }, [formData.content]);

  // Auto-fill SEO fields when excerpt/content changes (for meta description)
  useEffect(() => {
    if (formData.blogType !== 'Thought & Leadership') return;
    const excerpt = formData.excerpt.trim();
    const sourceText = excerpt || getPlainTextContent();
    if (!sourceText) return;

    const description = sourceText.length > 160 ? sourceText.substring(0, 157) + '...' : sourceText;

    // Auto-fill meta description if empty
    if (!formData.metaDescription.trim()) {
       
      setFormData(prev => ({
        ...prev,
        metaDescription: description
      }));
    }

    // Auto-fill OG description if empty
    if (!formData.ogDescription.trim()) {
      setFormData(prev => ({
        ...prev,
        ogDescription: description
      }));
    }

    // Auto-fill Twitter description if empty
    if (!formData.twitterDescription.trim()) {
      setFormData(prev => ({
        ...prev,
        twitterDescription: description
      }));
    }
  }, [formData.excerpt, formData.blogType, formData.metaDescription, formData.ogDescription, formData.twitterDescription, getPlainTextContent]);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time validation
    validateField(name, value);
  };


  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
      return;
    }

    setError(null);

    try {
      // Compress image if needed (will compress to below 1MB)
      const compressedFile = await compressImageIfNeeded(file, showToast);
      
      // Verify compression worked
      const maxSizeBytes = 1 * 1024 * 1024; // 1MB
      if (compressedFile.size > maxSizeBytes) {
        console.warn(`Compressed file is still ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB, attempting more aggressive compression...`);
        // Try one more time with very aggressive compression
        const moreCompressedFile = await compressImage(compressedFile, 0.95, 0.5);
        if (moreCompressedFile.size > maxSizeBytes) {
          throw new Error(`Unable to compress image below 1MB. Final size: ${(moreCompressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
        }
        const formDataUpload = new FormData();
        formDataUpload.append('file', moreCompressedFile);

        const response = await fetch('/api/upload/blogs', {
          method: 'POST',
          body: formDataUpload,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload image');
        }

        const result = await response.json();
        setFormData(prev => ({ ...prev, featuredImage: result.fileUrl }));

        // Clear the input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await fetch('/api/upload/blogs', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const result = await response.json();

      setFormData(prev => ({ ...prev, featuredImage: result.fileUrl }));

      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      console.error('Image upload error:', err);
    }
  };

  const handleOgImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
      return;
    }

    setError(null);

    try {
      // Compress image if needed (will compress to below 1MB)
      const compressedFile = await compressImageIfNeeded(file, showToast);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await fetch('/api/upload/blogs', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const result = await response.json();

      setFormData(prev => ({ ...prev, ogImage: result.fileUrl }));

      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    }
  };

  const handleTwitterImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
      return;
    }

    setError(null);

    try {
      // Compress image if needed (will compress to below 1MB)
      const compressedFile = await compressImageIfNeeded(file, showToast);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await fetch('/api/upload/blogs', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const result = await response.json();

      setFormData(prev => ({ ...prev, twitterImage: result.fileUrl }));

      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only MP4, WebM, OGG, MOV, and AVI videos are allowed.');
      return;
    }

    // Validate file size (max 50MB for videos)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 50MB for videos.');
      return;
    }

    setError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch('/api/upload/blogs', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload video');
      }

      const result = await response.json();

      // Insert video into editor
      if (editor && result.fileUrl) {
        const videoHtml = `<video src="${result.fileUrl}" controls style="max-width: 100%; height: auto; margin: 1rem 0;" preload="metadata"></video>`;
        editor.chain().focus().insertContent(videoHtml).run();
      }

      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload video');
    }
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    setFormData(prev => ({ ...prev, slug }));
  };

  const generateCanonicalUrl = useCallback(() => {
    if (!formData.slug.trim()) return;

    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'https://www.makemybrandlabs.com';
    let canonicalUrl = `${baseUrl}/blogs/${formData.slug}`;
    if (formData.blogType === 'Thought & Leadership') {
      canonicalUrl = `${baseUrl}/thought-leadership/${formData.slug}`;
    } else if (formData.blogType === 'News') {
      canonicalUrl = `${baseUrl}/news-pr/${formData.slug}`;
    }
    setFormData(prev => ({ ...prev, canonicalUrl }));
  }, [formData.slug, formData.blogType]);

  // Auto-generate canonical URL when slug changes and canonical URL is empty
  useEffect(() => {
    if (formData.slug && !formData.canonicalUrl.trim()) {
       
      generateCanonicalUrl();
    }
  }, [formData.slug, formData.canonicalUrl, generateCanonicalUrl]);

  const generateSchemaMarkup = useCallback(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": formData.blogType === 'News' ? "NewsArticle" : "BlogPosting",
      "headline": formData.title,
      "description": formData.metaDescription,
      ...((formData.blogType === 'Post' || formData.blogType === 'Thought & Leadership') && formData.author && {
        "author": {
          "@type": "Person",
          "name": formData.author
        }
      }),
      "datePublished": formData.publishDate,
      "image": formData.featuredImage
    };
    setFormData(prev => ({ ...prev, schemaMarkup: JSON.stringify(schema, null, 2) }));
  }, [formData.blogType, formData.title, formData.metaDescription, formData.author, formData.publishDate, formData.featuredImage]);

  // Auto-generate schema markup when title and excerpt are available
  useEffect(() => {
    if (formData.title.trim() && formData.excerpt.trim() && formData.blogType === 'Thought & Leadership' && !formData.schemaMarkup.trim()) {
       
      generateSchemaMarkup();
    }
  }, [formData.title, formData.excerpt, formData.blogType, formData.schemaMarkup, generateSchemaMarkup]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length < 5) {
      errors.title = 'Title must be at least 5 characters long';
    }

    if (!formData.slug.trim()) {
      errors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    } else if (formData.slug.length < 3) {
      errors.slug = 'Slug must be at least 3 characters long';
    }

    if (!formData.content || !formData.content.trim() || formData.content === '<p></p>') {
      errors.content = 'Content is required';
    } else {
      // Extract text content from HTML for validation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.content;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      if (textContent.trim().length < 100) {
        errors.content = 'Content must be at least 100 characters long';
      }
    }

    if (!formData.excerpt.trim()) {
      errors.excerpt = 'Excerpt is required';
    } else if (formData.excerpt.length < 50) {
      errors.excerpt = 'Excerpt must be at least 50 characters long';
    } else if (formData.excerpt.length > 300) {
      errors.excerpt = 'Excerpt must be less than 300 characters';
    }

    if (!effectiveAuthorName.trim() && formData.blogType === 'Post') {
      errors.author = 'Author is required for blog posts';
    }
    // Thought & Leadership and News don't require author

    if (formData.metaTitle && formData.metaTitle.length > 60) {
      errors.metaTitle = 'Meta title must be less than 60 characters';
    }

    if (formData.metaDescription && formData.metaDescription.length > 160) {
      errors.metaDescription = 'Meta description must be less than 160 characters';
    }

    if (formData.ogTitle && formData.ogTitle.length > 60) {
      errors.ogTitle = 'OG title must be less than 60 characters';
    }

    if (formData.ogDescription && formData.ogDescription.length > 160) {
      errors.ogDescription = 'OG description must be less than 160 characters';
    }

    if (formData.twitterTitle && formData.twitterTitle.length > 60) {
      errors.twitterTitle = 'Twitter title must be less than 60 characters';
    }

    if (formData.twitterDescription && formData.twitterDescription.length > 160) {
      errors.twitterDescription = 'Twitter description must be less than 160 characters';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the validation errors below');
      return false;
    }

    setError(null);
    return true;
  };


  const validateField = (name: string, value: string) => {
    const errors = { ...fieldErrors };

    switch (name) {
      case 'title':
        if (!value.trim()) {
          errors.title = 'Title is required';
        } else if (value.length < 5) {
          errors.title = 'Title must be at least 5 characters long';
        } else {
          delete errors.title;
        }
        break;
      case 'slug':
        if (!value.trim()) {
          errors.slug = 'Slug is required';
        } else if (!/^[a-z0-9-]+$/.test(value)) {
          errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
        } else if (value.length < 3) {
          errors.slug = 'Slug must be at least 3 characters long';
        } else {
          delete errors.slug;
        }
        break;
      case 'content':
        if (!value || !value.trim() || value === '<p></p>') {
          errors.content = 'Content is required';
        } else {
          // Extract text content from HTML for validation
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = value;
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          if (textContent.trim().length < 100) {
            errors.content = 'Content must be at least 100 characters long';
          } else {
            delete errors.content;
          }
        }
        break;
      case 'excerpt':
        if (!value.trim()) {
          errors.excerpt = 'Excerpt is required';
        } else if (value.length < 50) {
          errors.excerpt = 'Excerpt must be at least 50 characters long';
        } else if (value.length > 300) {
          errors.excerpt = 'Excerpt must be less than 300 characters';
        } else {
          delete errors.excerpt;
        }
        break;
      case 'author':
        if (!value.trim() && formData.blogType === 'Post') {
          errors.author = 'Author is required for blog posts';
        } else {
          delete errors.author;
        }
        // Thought & Leadership and News don't require author
        break;
      case 'metaTitle':
        if (value && value.length > 60) {
          errors.metaTitle = 'Meta title must be less than 60 characters';
        } else {
          delete errors.metaTitle;
        }
        break;
      case 'metaDescription':
        if (value && value.length > 160) {
          errors.metaDescription = 'Meta description must be less than 160 characters';
        } else {
          delete errors.metaDescription;
        }
        break;
      case 'ogTitle':
        if (value && value.length > 60) {
          errors.ogTitle = 'OG title must be less than 60 characters';
        } else {
          delete errors.ogTitle;
        }
        break;
      case 'ogDescription':
        if (value && value.length > 160) {
          errors.ogDescription = 'OG description must be less than 160 characters';
        } else {
          delete errors.ogDescription;
        }
        break;
      case 'twitterTitle':
        if (value && value.length > 60) {
          errors.twitterTitle = 'Twitter title must be less than 60 characters';
        } else {
          delete errors.twitterTitle;
        }
        break;
      case 'twitterDescription':
        if (value && value.length > 160) {
          errors.twitterDescription = 'Twitter description must be less than 160 characters';
        } else {
          delete errors.twitterDescription;
        }
        break;
    }

    setFieldErrors(errors);
  };

  const saveBlog = async (status?: 'Draft' | 'Published', skipValidation = false) => {
    const currentBlogType = formData.blogType || initialBlogType || 'Post';
    // Only validate if not skipping validation (i.e., when publishing)
    if (!skipValidation && !validateForm()) return false;
    if (currentBlogType === 'Post' && formData.instagramPostingEnabled) {
      const instagramErrors: Record<string, string> = {};
      if (!formData.instagramPostingCaption.trim()) {
        instagramErrors.instagramCaption = 'Instagram caption is required.';
      }
      const instagramImageUrl = formData.instagramPostingImageUrl.trim();
      if (!formData.instagramPostingUseFeaturedImage && !instagramImageUrl) {
        instagramErrors.instagramImage = 'Instagram image is required.';
      }
      if (formData.instagramPostingUseFeaturedImage && !formData.featuredImage.trim() && !instagramImageUrl) {
        instagramErrors.instagramImage = 'Add a featured image or provide a separate Instagram image URL.';
      }
      if (instagramImageUrl) {
        try {
          const parsedInstagramImageUrl = new URL(instagramImageUrl);
          if (!['http:', 'https:'].includes(parsedInstagramImageUrl.protocol)) {
            instagramErrors.instagramImage = 'Use a public HTTP or HTTPS image URL for Instagram.';
          }
        } catch {
          instagramErrors.instagramImage = 'Use a valid public image URL for Instagram.';
        }
      }
      if (!formData.instagramPostingScheduledAt) {
        instagramErrors.instagramSchedule = 'Schedule date and time are required.';
      }
      if (Object.keys(instagramErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...instagramErrors }));
        return false;
      }
    }

    setIsSaving(true);
    setError(null);

    try {
      // Determine API endpoint based on blogType
      // Use formData.blogType first, fall back to initialBlogType if not set
      const {
        socialMediaPostingTagsStr,
        socialMediaPostingEnabled: _omitSocialPostingEnabled,
        socialMediaPostingCaption,
        socialMediaPostingUseFeaturedImage,
        socialMediaPostingExtraImageUrls,
        socialMediaPostingScheduledAt,
        socialMediaPostingPublishedAt: _omitFbPublishedAt,
        facebookBlogAutoPostId: _omitFbId,
        facebookBlogAutoPostError: _omitFbErr,
        instagramPostingEnabled,
        instagramPublishType,
        instagramPostingCaption,
        instagramPostingTagsStr,
        instagramPostingUseFeaturedImage,
        instagramPostingImageUrl,
        instagramPostingScheduledAt,
        instagramPostingStatus: _omitIgStatus,
        instagramPostingPublishedAt: _omitIgPublishedAt,
        instagramMediaId: _omitIgMediaId,
        instagramPermalink: _omitIgPermalink,
        instagramUsername: _omitIgUsername,
        instagramLastSyncAt: _omitIgLastSyncAt,
        ...coreForm
      } = formData;

      const postingTags = socialMediaPostingTagsStr
        .split(/[,\n]/)
        .map((t) => t.trim())
        .filter(Boolean);
      const instagramTags = instagramPostingTagsStr
        .split(/[,\n]/)
        .map((t) => t.trim())
        .filter(Boolean);

      const blogData: Record<string, unknown> = {
        ...coreForm,
        author: effectiveAuthorName,
        editor: sessionRole === 'Author'
          ? formData.editor || ''
          : effectiveEditorName,
        status: status || formData.status,
        blogType: currentBlogType,
      };

      if (currentBlogType === 'Post') {
        const scheduledIso = datetimeLocalToIsoOrNull(socialMediaPostingScheduledAt);
        blogData.socialMediaPostingEnabled = !!scheduledIso;
        blogData.socialMediaPostingScheduledAt = scheduledIso;
        blogData.socialMediaPostingCaption = socialMediaPostingCaption;
        blogData.socialMediaPostingTags = postingTags;
        blogData.socialMediaPostingUseFeaturedImage = socialMediaPostingUseFeaturedImage;
        blogData.socialMediaPostingExtraImageUrls = socialMediaPostingExtraImageUrls;
        blogData.instagramPostingEnabled = instagramPostingEnabled;
        blogData.instagramPublishType = instagramPublishType;
        blogData.instagramPostingCaption = instagramPostingCaption;
        blogData.instagramPostingTags = instagramTags;
        blogData.instagramPostingUseFeaturedImage = instagramPostingUseFeaturedImage;
        blogData.instagramPostingImageUrl = instagramPostingImageUrl;
        blogData.instagramPostingScheduledAt = instagramPostingScheduledAt
          ? new Date(instagramPostingScheduledAt).toISOString()
          : null;
      }

      let baseUrl = '/api/admin/blogs';
      let contentType = 'blog';
      if (currentBlogType === 'Thought & Leadership') {
        baseUrl = '/api/admin/thought-leadership';
        contentType = 'thought leadership';
      } else if (currentBlogType === 'News') {
        baseUrl = '/api/admin/news-pr';
        contentType = 'news';
      }

      const url = blogId ? `${baseUrl}/${blogId}` : baseUrl;
      const method = blogId ? 'PUT' : 'POST';


      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(blogData),
      });


      // Try to parse response
      let result;
      try {
        const responseText = await response.text();
        result = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        if (!response.ok) {
          throw new Error(`Failed to save ${contentType}: ${response.status} ${response.statusText}`);
        }
        // If response is ok but can't parse, assume success
        result = { success: true };
      }

      if (!response.ok) {
        console.error('Save blog error response:', result);
        const errorMsg = result.error || result.message || `Failed to save ${contentType}: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }


      // Check if there's an error in the response (even with 200 status)
      if (result.error) {
        throw new Error(result.error);
      }

      // If response is successful (200-299), proceed
      // If creating new blog, redirect to edit mode
      const newId = result.blogId || result.thoughtLeadershipId || result.newsPrId;
      if (!blogId && newId) {
        // Determine redirect path based on blogType
        let redirectPath = `/admin/blogs/${newId}/edit`;
        if (formData.blogType === 'Thought & Leadership') {
          redirectPath = `/admin/thought-leadership/${newId}/edit`;
        } else if (formData.blogType === 'News') {
          redirectPath = `/admin/news-pr/${newId}/edit`;
        }
        router.push(redirectPath);
        return {
          success: true,
          blogId: newId,
          thoughtLeadershipId: result.thoughtLeadershipId,
          newsPrId: result.newsPrId,
        };
      } else {
        // For updates, return the ID and result
        // For updates, blogId should already be set from props
        // Return object with success flag and the ID (use blogId from props for updates)
        const returnId = blogId || result.blogId || result.thoughtLeadershipId || result.newsPrId;
        if (returnId) {
          return {
            success: true,
            blogId: returnId,
            thoughtLeadershipId: result.thoughtLeadershipId,
            newsPrId: result.newsPrId,
          };
        } else {
          // If somehow we don't have an ID, still return success but log a warning
          console.warn('Update successful but no ID found. blogId prop:', blogId, 'result:', result);
          return { success: true };
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save blog';
      console.error('Error in saveBlog:', err);
      setError(errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    // Clear all field errors when saving as draft
    setFieldErrors({});
    setError(null);

    // Skip all validations when saving as draft
    const result = await saveBlog('Draft', true);
    if (result) {
      // Redirect to appropriate table based on blogType
      let redirectPath = '/admin/blogs';
      if (formData.blogType === 'Thought & Leadership') {
        redirectPath = '/admin/thought-leadership';
      } else if (formData.blogType === 'News') {
        redirectPath = '/admin/news-pr';
      }
      router.push(redirectPath);
    }
  };

  const handlePublish = async () => {
    // Clear any previous errors
    setError(null);
    setFieldErrors({});

    const isPublished = formData.status === 'Published';

    // Admin can publish directly (existing behaviour)
    // Editors can directly update already published posts
    if (sessionRole === 'Admin' || (sessionRole === 'Editor' && isPublished)) {
      // First validate basic form fields
      const formValid = validateForm();
      if (!formValid) {
        setError('Please fix the form errors before publishing.');
        return;
      }

      // Then check if SEO settings are complete
      const seoValidation = validateSEOComplete();
      if (!seoValidation.isComplete) {
        const missingFieldsText = seoValidation.missingFields.join(', ');
        setError(
          `Cannot publish ${formData.blogType === 'Thought & Leadership' ? 'thought leadership' : 'blog'
          }. Please complete the following SEO settings: ${missingFieldsText}`
        );
        // Scroll to SEO settings section
        const seoElement = document.querySelector('[data-seo-section]');
        if (seoElement) {
          seoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      // All validations passed, save and publish
      try {
        const result = await saveBlog('Published', false);
        const isSuccess =
          result !== false &&
          (typeof result === 'object' &&
            result !== null &&
            (result.success === true || result.success === undefined));

        if (isSuccess) {
          const isNewContent = !blogId;

          if (isNewContent) {
            const contentId =
              typeof result === 'object'
                ? result?.blogId || result?.thoughtLeadershipId || result?.newsPrId
                : null;

            let contentType = 'blog';
            if (formData.blogType === 'Thought & Leadership') {
              contentType = 'thought-leadership';
            } else if (formData.blogType === 'News') {
              contentType = 'news';
            }

            if (contentId) {
              localStorage.setItem(
                'pendingNewsletter',
                JSON.stringify({
                  contentId,
                  contentType,
                  timestamp: Date.now()
                })
              );
            }
          } else {
          }

          await new Promise(resolve => setTimeout(resolve, 300));

          let redirectPath = '/admin/blogs';
          if (formData.blogType === 'Thought & Leadership') {
            redirectPath = '/admin/thought-leadership';
          } else if (formData.blogType === 'News') {
            redirectPath = '/admin/news-pr';
          }
          window.location.href = redirectPath;
        } else {
          console.error('Save blog returned false. Check error state and console for details.');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to publish. Please try again.';
        setError(errorMsg);
        console.error('Publish error:', err);
      }
      return;
    }

    // Editor / Author flow: save as Draft and create a publish or update request for Admin

    // We still require a valid form before sending a publish request
    const formValid = validateForm();
    if (!formValid) {
      setError('Please fix the form errors before requesting publish.');
      return;
    }

    try {
      const saveResult = await saveBlog('Draft', false);

      if (!saveResult) {
        return;
      }

      let createdId: string | undefined;
      if (typeof saveResult === 'object' && saveResult !== null) {
        createdId = saveResult.blogId || saveResult.thoughtLeadershipId || saveResult.newsPrId;
      } else if (blogId) {
        createdId = blogId;
      }

      if (!createdId) {
        console.warn('No blog ID available after saving draft. Cannot create workflow request.');
        setError('Unable to create publish request – missing blog ID.');
        return;
      }

      const response = await fetch('/api/admin/posts/workflow/publish-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          postId: createdId,
          postType: 'blog',
          title: formData.title,
          authorName: effectiveAuthorName,
          // For Author role, editor is assigned later by Admin, so keep this empty
          editorName: sessionRole === 'Author'
            ? ''
            : effectiveEditorName
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const msg = data.error || 'Failed to create publish request';
        setError(msg);
        console.error('Publish request API error:', msg);
        return;
      }

      const requestLabel =
        sessionRole === 'Author'
          ? 'Review & Assign Editor Request'
          : 'Review & Publish Request';
      setInfoMessage(`${requestLabel} sent to Admin.`);
      // Small delay then redirect back to blogs list
      await new Promise(resolve => setTimeout(resolve, 300));
      window.location.href = '/admin/blogs';
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to create publish request. Please try again.';
      setError(errorMsg);
      console.error('Publish request error:', err);
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <div className="text-center space-y-6">
          {/* Animated Loading Spinner */}
          <div className="relative">
            <div className="w-16 h-16 mx-auto">
              <div className="absolute inset-0 border-4 border-sky-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-sky-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
          </div>

          {/* Animated Dots */}
          <div className="flex items-center justify-center gap-2">
            <div className="h-2 w-2 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="h-2 w-2 rounded-full bg-sky-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="h-2 w-2 rounded-full bg-sky-600 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>

          {/* Loading Text */}
          <div className="space-y-2">
            <p className="text-lg font-semibold text-gray-800">Loading blog editor</p>
            <p className="text-sm text-gray-600">Preparing your workspace...</p>
          </div>
        </div>
      </div>
    );
  }


  if (showPreview) {
    return (
      <BlogPreview
        formData={{
          title: formData.title,
          publishDate: formData.publishDate,
          author: formData.author,
          tags: formData.tags,
          excerpt: formData.excerpt,
          content: formData.content,
          featuredImage: formData.featuredImage,
          featuredImageAlt: formData.featuredImageAlt
        }}
        onBackToEdit={() => setShowPreview(false)}
      />
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Keyword highlighting styles */}
      <style jsx>{`
        .keyword-editor .keyword-highlight {
          background-color: #fef3c7;
          border-bottom: 2px solid #f59e0b;
          padding: 2px 0;
          border-radius: 2px;
        }
        .keyword-editor .keyword-highlight[data-keyword] {
          position: relative;
        }
        .keyword-editor .keyword-highlight[data-keyword]:hover::after {
          content: attr(data-keyword);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #374151;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 10;
        }
      `}</style>

      <div className="space-y-6 p-4">
        {/* Error Message Display */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl shadow-sm p-4 backdrop-blur-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex text-red-400 hover:text-red-600 transition-colors"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Workflow Info Notice (rework / rejection / publish request) */}
        {infoMessage && (
          <div className="bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-xl shadow-sm p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
              <p className="text-sm  text-sky-800">{infoMessage}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {(() => {
          const seoValidation = validateSEOComplete();
          const items: ChecklistItem[] = [
            { id: 'title', label: 'Add a title (≥ 5 characters)', done: !fieldErrors.title && !!formData.title.trim() && formData.title.length >= 5 },
            { id: 'slug', label: 'Provide a valid slug (lowercase, numbers, hyphens, ≥ 3)', done: !fieldErrors.slug && !!formData.slug.trim() && /^[a-z0-9-]{3,}$/.test(formData.slug) },
            { id: 'content', label: 'Write content (≥ 100 characters)', done: !fieldErrors.content && !!formData.content && formData.content !== '<p></p>' },
            { id: 'excerpt', label: 'Add an excerpt (50-300 characters)', done: !fieldErrors.excerpt && !!formData.excerpt.trim() && formData.excerpt.length >= 50 && formData.excerpt.length <= 300 },
            { id: 'featuredImage', label: 'Upload a featured image', done: !!(formData.featuredImage || '').trim() },
            { id: 'featuredImageAlt', label: 'Featured Image Alternate Text', done: !fieldErrors.featuredImageAlt && !!formData.featuredImageAlt.trim() },
            { id: 'category', label: 'Select a category', done: !fieldErrors.tags && formData.tags && formData.tags.length > 0 },
            { id: 'status', label: 'Set status', done: !fieldErrors.status && !!formData.status },
            { id: 'publishDate', label: 'Set publish date & time', done: !fieldErrors.publishDate && !fieldErrors.publishTime && !!formData.publishDate.trim() && !!formData.publishTime.trim() },
            { id: 'author', label: 'Author is set', done: !!formData.author.trim() },
            { id: 'canonicalUrl', label: 'Canonical URL', done: !fieldErrors.canonicalUrl && !!formData.canonicalUrl.trim() },
          ];

          // Add SEO requirements when publishing state is relevant; we still show them as helpful checklist
          const seoItems: ChecklistItem[] = [
            { id: 'metaTitle', label: 'SEO: Meta Title', done: !!formData.metaTitle.trim() && !fieldErrors.metaTitle },
            { id: 'metaDescription', label: 'SEO: Meta Description', done: !!formData.metaDescription.trim() && !fieldErrors.metaDescription },
            { id: 'focusKeywords', label: 'SEO: Focus Keywords (Optional)', done: !!formData.focusKeywords.trim() },
            { id: 'canonicalUrl', label: 'SEO: Canonical URL', done: !!formData.canonicalUrl.trim() && !fieldErrors.canonicalUrl },
            { id: 'ogTitle', label: 'SEO: Open Graph Title', done: !!formData.ogTitle.trim() && !fieldErrors.ogTitle },
            { id: 'ogDescription', label: 'SEO: Open Graph Description', done: !!formData.ogDescription.trim() && !fieldErrors.ogDescription },
            { id: 'ogImage', label: 'SEO: Open Graph Image', done: !!formData.ogImage.trim() },
            { id: 'twitterTitle', label: 'SEO: Twitter Title', done: !!formData.twitterTitle.trim() && !fieldErrors.twitterTitle },
            { id: 'twitterDescription', label: 'SEO: Twitter Description', done: !!formData.twitterDescription.trim() && !fieldErrors.twitterDescription },
            { id: 'twitterImage', label: 'SEO: Twitter Image', done: !!formData.twitterImage.trim() },
            { id: 'schemaMarkup', label: 'SEO: Schema Markup', done: !!formData.schemaMarkup.trim() },
          ];

          const combined = [...items, ...seoItems];
          const checklistComplete = combined.every(i => i.done);
          // Match Thought Leadership visibility: show when anything incomplete OR errors present
          const showChecklist = !checklistComplete || error !== null || Object.keys(fieldErrors).length > 0 || !seoValidation.isComplete;

          return (
            <>
              <ActionButtons
                status={formData.status}
                seoComplete={seoComplete}
                checklistComplete={checklistComplete}
                isSaving={isSaving}
                showPreview={showPreview}
                onTogglePreview={() => setShowPreview(!showPreview)}
                onSaveDraft={handleSaveDraft}
                onPublish={handlePublish}
                publishLabel={
                  sessionRole === 'Admin'
                    ? 'Publish'
                    : sessionRole === 'Editor'
                      ? formData.status === 'Published'
                        ? 'Publish'
                        : 'Raise Review & Publish Request'
                      : formData.status === 'Published'
                        ? 'Raise Update Request'
                        : 'Raise Review & Assign Editor Request'
                }
              />

              {/* Floating checklist (aligned with Thought Leadership editor) */}
              {showChecklist && (
                <FloatingChecklist
                  title="Publishing Checklist"
                  items={combined}
                  hideWhenComplete={true}
                  onItemClick={(id: string) => {
                    const byName = (name: string) => document.querySelector(`[name="${name}"]`) as HTMLElement | null;
                    const smoothScroll = (el: HTMLElement | null) => {
                      if (!el) return;
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      if ('focus' in el) {
                        try { (el as HTMLInputElement).focus({ preventScroll: true }); } catch { }
                      }
                    };

                    switch (id) {
                      case 'title':
                        return smoothScroll(byName('title'));
                      case 'slug':
                        return smoothScroll(byName('slug'));
                      case 'content': {
                        const anchor = document.getElementById('content-editor-anchor');
                        smoothScroll(anchor);
                        const prose = document.querySelector('.ProseMirror') as HTMLElement | null;
                        if (prose) prose.focus();
                        return;
                      }
                      case 'excerpt':
                        return smoothScroll(byName('excerpt'));
                      case 'featuredImage': {
                        // Scroll near the featured image upload area; it's just above the alt text field
                        return smoothScroll(document.querySelector('input[name="featuredImageAlt"]') as HTMLElement | null);
                      }
                      case 'featuredImageAlt':
                        return smoothScroll(byName('featuredImageAlt'));
                      case 'category': {
                        const categorySelect = document.querySelector('select[value]') || document.querySelector('select');
                        return smoothScroll(categorySelect as HTMLElement | null);
                      }
                      case 'status':
                        return smoothScroll(byName('status'));
                      case 'publishDate':
                        return smoothScroll(byName('publishDate'));
                      case 'author':
                        return smoothScroll(byName('author'));
                      // SEO fields (scroll to SEO section first)
                      case 'metaTitle':
                      case 'metaDescription':
                      case 'focusKeywords':
                      case 'canonicalUrl':
                      case 'ogTitle':
                      case 'ogDescription':
                      case 'ogImage':
                      case 'twitterTitle':
                      case 'twitterDescription':
                      case 'twitterImage':
                      case 'schemaMarkup':
                        smoothScroll(document.querySelector('[data-seo-section]') as HTMLElement | null);
                        return smoothScroll(byName(id));
                    }
                  }}
                />
              )}
            </>
          );
        })()}

        {/* Single Unified Page Container - Equal Heights */}
        <div className="bg-white/60 backdrop-blur-md rounded-2xl shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[800px]">
            {/* Main Content Area */}
            <div className="lg:col-span-2 p-8 space-y-8 border-r border-sky-200/30 flex flex-col">
              {/* Basic Information Section */}
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                    Title <span className="text-sky-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full border-2 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none ${fieldErrors.title
                      ? 'border-red-300 bg-red-50/50'
                      : 'border-sky-200 bg-white hover:border-sky-300'
                      }`}
                    placeholder="Enter blog title"
                  />
                  {fieldErrors.title && (
                    <p className="mt-1.5 text-sm  text-red-600 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.title}
                    </p>
                  )}
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                    Slug/URL <span className="text-sky-600">*</span>
                  </label>
                  <div className="flex space-x-2">
                    <span className="inline-flex items-center px-4 py-2.5 border-2 border-r-0 border-sky-200 bg-gradient-to-r from-sky-50 to-blue-50 text-sky-700 text-sm font-semibold rounded-l-lg">
                      /
                    </span>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      className={`flex-1 border-2 rounded-r-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none ${fieldErrors.slug
                        ? 'border-red-300 bg-red-50/50'
                        : 'border-sky-200 bg-white hover:border-sky-300'
                        }`}
                      placeholder="blog-slug"
                    />
                    <button
                      type="button"
                      onClick={generateSlug}
                      className="px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all font-semibold text-sm shadow-sm hover:shadow-md"
                    >
                      Generate
                    </button>
                  </div>
                  {fieldErrors.slug && (
                    <p className="mt-1.5 text-sm  text-red-600 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.slug}
                    </p>
                  )}
                </div>

                {/* Content Editor */}
                <div id="content-editor-anchor">
                  <ContentEditor
                    editor={editor}
                    editorReady={editorReady}
                    fieldErrors={fieldErrors}
                    handleVideoUpload={handleVideoUpload}
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                    Excerpt <span className="text-sky-600">*</span>
                    <span className="ml-2 text-xs  text-sky-600">
                      ({formData.excerpt.length}/300 characters)
                    </span>
                  </label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full border-2 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none resize-none ${fieldErrors.excerpt
                      ? 'border-red-300 bg-red-50/50'
                      : 'border-sky-200 bg-white hover:border-sky-300'
                      }`}
                    placeholder="Brief summary of your blog post"
                  />
                  {fieldErrors.excerpt && (
                    <p className="mt-1.5 text-sm  text-red-600 flex items-center gap-1">
                      <span>⚠️</span> {fieldErrors.excerpt}
                    </p>
                  )}
                </div>

                {/* Divider */}
                <div className="my-8 border-t border-sky-200/50"></div>

                {/* Media & Publishing Section */}
                {/* Featured Image */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                    Featured Image
                  </label>
                  <div className="space-y-2">
                    {formData.featuredImage ? (
                      <div className="relative group">
                        <Image
                          src={formData.featuredImage}
                          alt={formData.featuredImageAlt || 'Featured image'}
                          width={400}
                          height={192}
                          className="w-full h-48 object-cover rounded-lg shadow-md border-2 border-sky-200"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, featuredImage: '' }))}
                          className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all shadow-lg hover:shadow-xl"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-sky-300 rounded-xl p-8 text-center bg-gradient-to-br from-sky-50/50 to-blue-50/50 hover:from-sky-100/50 hover:to-blue-100/50 transition-all">
                        <Upload className="mx-auto h-12 w-12 text-sky-400" />
                        <div className="mt-3">
                          <label className="cursor-pointer">
                            <span className="text-sky-600 hover:text-sky-700 font-semibold text-sm transition-colors">Upload an image</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-gray-500 mt-1 ">PNG, JPG, GIF up to 10MB</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Featured Image Alt Text */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                    Featured Image Alternate Text <span className="text-sky-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="featuredImageAlt"
                    value={formData.featuredImageAlt}
                    onChange={handleInputChange}
                    className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                    placeholder="Describe the image for accessibility and SEO"
                  />
                  <p className="mt-1.5 text-xs text-gray-500 ">Used for accessibility and image alt attribute.</p>
                </div>

                {/* Post Type (fixed to Blog) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                    Post Type <span className="text-sky-600">*</span>
                  </label>
                  <input
                    type="text"
                    value="Blog"
                    readOnly
                    className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 bg-gradient-to-r from-sky-50 to-blue-50 text-gray-900 font-normal text-sm cursor-not-allowed"
                  />
                </div>

                {/* Author, Editor & Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-800 tracking-tight">
                        Author {formData.blogType === 'Post' ? <span className="text-sky-600">*</span> : <span className="text-gray-400">(Optional)</span>}
                      </label>
                    </div>
                    <select
                      name="author"
                      value={effectiveAuthorName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, author: value }));
                        validateField('author', value);
                      }}
                      disabled={availableAuthors.length === 0 || lockAuthorForEditor}
                      className={`w-full border-2 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none ${fieldErrors.author ? 'border-red-300 bg-red-50/50' : 'border-sky-200 bg-white hover:border-sky-300'
                        } ${availableAuthors.length === 0 || lockAuthorForEditor
                          ? 'bg-sky-50 cursor-not-allowed text-gray-500'
                          : ''
                        }`}
                    >
                      <option value="">Select an author</option>
                      {!availableAuthors.some(author => author.fullName === effectiveAuthorName) && effectiveAuthorName && (
                        <option value={effectiveAuthorName}>{effectiveAuthorName} (legacy)</option>
                      )}
                      {availableAuthors.map((author) => (
                        <option key={author.id} value={author.fullName}>
                          {author.fullName}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-sm text-gray-500 ">
                      Authors are managed in the Author Personal Data module
                    </p>
                    {availableAuthors.length === 0 && (
                      <p className="mt-1.5 text-xs text-amber-600  flex items-center gap-1">
                        <span>⚠️</span> No author profiles available. Please add author personal data first.
                      </p>
                    )}
                    {fieldErrors.author && (
                      <p className="mt-1.5 text-sm  text-red-600 flex items-center gap-1">
                        <span>⚠️</span> {fieldErrors.author}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                      Editor <span className="text-sky-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="editor"
                      value={effectiveEditorName}
                      disabled
                      readOnly
                      className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 bg-gradient-to-r from-sky-50 to-blue-50 text-gray-700 font-normal text-sm cursor-not-allowed"
                    />
                    <p className="mt-1.5 text-xs text-gray-500 ">
                      {sessionRole === 'Author'
                        ? 'Editor will be assigned by Admin during review'
                        : 'Editor is fixed to your account'}
                    </p>
                  </div>

                  {/* Category & Publish Date/Time in one row */}
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                        Category <span className="text-sky-600">*</span>
                      </label>
                      <div className="space-y-2">
                        <div className="relative">
                          <div className="flex space-x-2">
                            <div className="flex-1 relative">
                              <select
                                value={newTag}
                                onChange={(e) => {
                                  const selectedCategory = e.target.value;
                                  setNewTag(selectedCategory);
                                  if (selectedCategory) {
                                    // Allow only ONE category
                                    setFormData(prev => ({ ...prev, tags: [selectedCategory] }));
                                    setNewTag('');
                                  }
                                }}
                                className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                              >
                                <option value="">Select category</option>
                                {filteredCategories.length === 0 ? (
                                  <option value="" disabled>
                                    No categories available for {formData.blogType} type
                                  </option>
                                ) : (
                                  filteredCategories.map((category: { name: string }, index: number) => (
                                    <option key={index} value={category.name}>
                                      {category.name}
                                    </option>
                                  ))
                                )}
                              </select>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mb-2 ">
                          Select one category from the dropdown above. Only one category is allowed.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {formData.tags.slice(0, 1).map(tag => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700 border border-sky-200 shadow-sm"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="ml-2 text-sky-600 hover:text-sky-800 transition-colors font-bold"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Publish Date & Time */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
                        Publish Date & Time <span className="text-sky-600">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          name="publishDate"
                          value={formData.publishDate}
                          onChange={handleInputChange}
                          className="border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                        />
                        <input
                          type="time"
                          name="publishTime"
                          value={formData.publishTime}
                          onChange={handleInputChange}
                          className="border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Same Height as Main Content */}
            <div className="p-8 space-y-8 flex flex-col overflow-y-auto">
              <SEOSettings
                formData={{
                  metaTitle: formData.metaTitle,
                  metaDescription: formData.metaDescription,
                  focusKeywords: formData.focusKeywords,
                  canonicalUrl: formData.canonicalUrl,
                  ogTitle: formData.ogTitle,
                  ogDescription: formData.ogDescription,
                  ogImage: formData.ogImage,
                  twitterTitle: formData.twitterTitle,
                  twitterDescription: formData.twitterDescription,
                  twitterImage: formData.twitterImage,
                  schemaMarkup: formData.schemaMarkup,
                  enableSchema: formData.enableSchema,
                  blogType: formData.blogType
                }}
                keywordDensity={keywordDensity}
                editor={editor}
                handleInputChange={handleInputChange}
                handleOgImageUpload={handleOgImageUpload}
                handleTwitterImageUpload={handleTwitterImageUpload}
                generateSchemaMarkup={generateSchemaMarkup}
                calculateKeywordDensity={calculateKeywordDensity}
                generateCanonicalUrl={generateCanonicalUrl}
                afterSocialMediaBlock={
                  formData.blogType === 'Post' ? (
                    <>
                      <BlogSocialMediaPostingSection
                        featuredImage={formData.featuredImage}
                        enabled={formData.socialMediaPostingEnabled}
                        scheduledAt={formData.socialMediaPostingScheduledAt}
                        publishedAt={formData.socialMediaPostingPublishedAt}
                        useFeaturedImage={formData.socialMediaPostingUseFeaturedImage}
                        caption={formData.socialMediaPostingCaption}
                        tagsStr={formData.socialMediaPostingTagsStr}
                        extraImageUrls={formData.socialMediaPostingExtraImageUrls}
                        {...(formData.facebookBlogAutoPostId
                          ? { facebookBlogAutoPostId: formData.facebookBlogAutoPostId }
                          : {})}
                        {...(formData.facebookBlogAutoPostError
                          ? { facebookBlogAutoPostError: formData.facebookBlogAutoPostError }
                          : {})}
                        showToast={showToast}
                        onScheduledAtChange={(v) =>
                          setFormData((p) => ({
                            ...p,
                            socialMediaPostingScheduledAt: v,
                            socialMediaPostingEnabled: !!v,
                          }))
                        }
                        onUseFeaturedChange={(v) => setFormData((p) => ({ ...p, socialMediaPostingUseFeaturedImage: v }))}
                        onCaptionChange={(v) => setFormData((p) => ({ ...p, socialMediaPostingCaption: v }))}
                        onTagsStrChange={(v) => setFormData((p) => ({ ...p, socialMediaPostingTagsStr: v }))}
                        onExtraUrlsChange={(urls) => setFormData((p) => ({ ...p, socialMediaPostingExtraImageUrls: urls }))}
                      />
                      <InstagramManagementCard
                        featuredImage={formData.featuredImage}
                        showToast={showToast}
                        value={{
                          enabled: formData.instagramPostingEnabled,
                          publishType: formData.instagramPublishType,
                          caption: formData.instagramPostingCaption,
                          tagsStr: formData.instagramPostingTagsStr,
                          useFeaturedImage: formData.instagramPostingUseFeaturedImage,
                          imageUrl: formData.instagramPostingImageUrl,
                          scheduledAt: formData.instagramPostingScheduledAt,
                          status: formData.instagramPostingStatus,
                          publishedAt: formData.instagramPostingPublishedAt,
                          mediaId: formData.instagramMediaId,
                          permalink: formData.instagramPermalink,
                          username: formData.instagramUsername,
                          lastSyncAt: formData.instagramLastSyncAt,
                        }}
                        validationErrors={{
                          ...(fieldErrors.instagramCaption ? { caption: fieldErrors.instagramCaption } : {}),
                          ...(fieldErrors.instagramImage ? { image: fieldErrors.instagramImage } : {}),
                          ...(fieldErrors.instagramSchedule ? { schedule: fieldErrors.instagramSchedule } : {}),
                        }}
                        onChange={(next: InstagramManagementValue) => {
                          setFieldErrors((prev) => {
                            const { instagramCaption, instagramImage, instagramSchedule, ...rest } = prev;
                            void instagramCaption;
                            void instagramImage;
                            void instagramSchedule;
                            return rest;
                          });
                          setFormData((p) => ({
                            ...p,
                            instagramPostingEnabled: next.enabled,
                            instagramPublishType: next.publishType,
                            instagramPostingCaption: next.caption,
                            instagramPostingTagsStr: next.tagsStr,
                            instagramPostingUseFeaturedImage: next.useFeaturedImage,
                            instagramPostingImageUrl: next.imageUrl,
                            instagramPostingScheduledAt: next.scheduledAt,
                          }));
                        }}
                      />
                    </>
                  ) : null
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Link Modal */}
      <LinkModal
        isOpen={isLinkModalOpen}
        onCancel={() => setIsLinkModalOpen(false)}
        initialUrl={initialLinkUrl}
        initialRel={initialLinkRel}
        initialOpenInNewTab={initialOpenInNewTab}
        onConfirm={onConfirmLink}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
